export const semanticColors = {
  bg: {
    default: {
      _light: "gray.50",
      _dark: "black",
    },
    emphasized: {
      _light: "gray.100",
      _dark: "black",
    },
    container: {
      _light: "white",
      _dark: "gray.950",
    },
    inverted: {
      _light: "black",
      _dark: "gray.50",
    },
    code: {
      _light: "black",
      _dark: "black",
    },
  },

  // Grain colors for the decorative DitherGradient. Light values match the
  // neutral/indigo subtle tones; dark values are lifted off bg.container so
  // the dither stays visible against the near-black dark background.
  dither: {
    neutral: {
      _light: "gray.100",
      _dark: "gray.800",
    },
    highlight: {
      _light: "indigo.200",
      _dark: "indigo.700",
    },
  },

  border: {
    container: {
      _light: "gray.100",
      _dark: "gray.800",
    },
    divider: {
      _light: "gray.100",
      _dark: "gray.800",
    },
    emphasized: {
      _light: "gray.200",
      _dark: "gray.800",
    },
    inverted: {
      _light: "black",
      _dark: "white",
    },
    contrast: {
      _light: "gray.50",
      _dark: "gray.50",
    },
  },

  fg: {
    default: {
      _light: "black",
      _dark: "gray.50",
    },
    emphasized: {
      _light: "gray.700",
      _dark: "gray.400",
    },
    muted: {
      _light: "gray.600",
      _dark: "gray.500",
    },
    subtle: {
      _light: "gray.400",
      _dark: "gray.600",
    },
    inverted: {
      _light: "gray.50",
      _dark: "black",
    },
    contrast: {
      _light: "gray.50",
      _dark: "gray.50",
    },
    code: {
      _light: "gray.50",
      _dark: "gray.50",
    },
  },

  neutral: {
    default: {
      _light: "gray.50",
      _dark: "black",
    },
    subtle: {
      _light: "gray.100",
      _dark: "gray.900",
    },
    muted: {
      _light: "gray.200",
      _dark: "gray.800",
    },
    emphasized: {
      _light: "gray.300",
      _dark: "gray.700",
    },
    focusRing: {
      _light: "gray.200",
      _dark: "gray.800",
    },
    fg: {
      _light: "gray.700",
      _dark: "gray.400",
    },
    solid: {
      _light: "gray.500",
      _dark: "gray.700",
    },
  },

  highlight: {
    default: {
      _light: "indigo.50",
      _dark: "indigo.950",
    },
    subtle: {
      _light: "indigo.100",
      _dark: "indigo.900",
    },
    muted: {
      _light: "indigo.200",
      _dark: "indigo.800",
    },
    emphasized: {
      _light: "indigo.300",
      _dark: "indigo.700",
    },
    focusRing: {
      _light: "indigo.200",
      _dark: "indigo.800",
    },
    fg: {
      _light: "indigo.600",
      _dark: "indigo.400",
    },
    solid: {
      _light: "indigo.500",
      _dark: "indigo.600",
    },
  },

  success: {
    default: {
      _light: "green.50",
      _dark: "green.950",
    },
    subtle: {
      _light: "green.100",
      _dark: "green.900",
    },
    muted: {
      _light: "green.200",
      _dark: "green.800",
    },
    emphasized: {
      _light: "green.300",
      _dark: "green.700",
    },
    focusRing: {
      _light: "green.200",
      _dark: "green.800",
    },
    fg: {
      _light: "green.600",
      _dark: "green.400",
    },
    solid: {
      _light: "green.500",
      _dark: "green.600",
    },
  },

  warning: {
    default: {
      _light: "sand.50",
      _dark: "sand.950",
    },
    subtle: {
      _light: "sand.100",
      _dark: "sand.900",
    },
    muted: {
      _light: "sand.200",
      _dark: "sand.800",
    },
    emphasized: {
      _light: "sand.300",
      _dark: "sand.700",
    },
    focusRing: {
      _light: "sand.200",
      _dark: "sand.800",
    },
    fg: {
      _light: "sand.600",
      _dark: "sand.500",
    },
    fgEmphasized: {
      _light: "sand.700",
      _dark: "sand.400",
    },
    solid: {
      _light: "sand.500",
      _dark: "sand.600",
    },
  },

  error: {
    default: {
      _light: "red.100",
      _dark: "red.950",
    },
    subtle: {
      _light: "red.200",
      _dark: "red.900",
    },
    muted: {
      _light: "red.300",
      _dark: "red.800",
    },
    emphasized: {
      _light: "red.400",
      _dark: "red.700",
    },
    focusRing: {
      _light: "red.200",
      _dark: "red.800",
    },
    fg: {
      _light: "red.600",
      _dark: "red.500",
    },
    solid: {
      _light: "red.500",
      _dark: "red.600",
    },
  },

  fatal: {
    emphasized: {
      _light: "pink.300",
      _dark: "pink.400",
    },
    solid: {
      _light: "pink.600",
      _dark: "pink.700",
    },
  },

  gradient: {
    blackToHighlight: {
      90100:
        "linear-gradient(90deg, var(--chakra-colors-black), var(--chakra-colors-highlight-solid)) 0 0/100% 100%",
      141100: "linear-gradient(141deg, #000000,#745AFF) 0 0/100% 100%",
    },
    highlight: {
      90400:
        "linear-gradient(90deg, var(--chakra-colors-highlight-solid), var(--chakra-colors-highlight-emphasized)) 0 0/400% 100%",
    },
    highlightToPink: {
      90: {
        _light:
          "linear-gradient(90deg, var(--chakra-colors-indigo-100) 0%, var(--chakra-colors-pink-100) 100%)",
        _dark:
          "linear-gradient(90deg, var(--chakra-colors-indigo-900) 0%, var(--chakra-colors-pink-900) 100%)",
      },
    },
    popSql: {
      default: "linear-gradient(180deg, #FD8C3D 0%, #EA4476 100%)",
      hover: "linear-gradient(180deg, #FD8C3D 0%, #EA4476 70%, #EA4476 100%)",
      active: "linear-gradient(180deg, #FD8C3D 0%, #EA4476 30%, #EA4476 100%)",
    },
    softGradient: {
      90200: {
        _light:
          "linear-gradient(90deg, var(--chakra-colors-indigo-100) 0%, var(--chakra-colors-bg-container) 50%, var(--chakra-colors-pink-100) 100%) 0 0/200% 100%",
        _dark:
          "linear-gradient(90deg, var(--chakra-colors-indigo-900) 0%, var(--chakra-colors-bg-container) 50%, var(--chakra-colors-pink-900) 100%) 0 0/200% 100%",
      },
    },
    timescale: {
      _light:
        "linear-gradient(90deg, var(--chakra-colors-highlight-fg) 0%, var(--chakra-colors-pink-600) 100%)",
      _dark:
        "linear-gradient(90deg, var(--chakra-colors-highlight-fg) 0%, var(--chakra-colors-pink-500) 100%)",
    },
    popsql: {
      _light:
        "linear-gradient(90deg, var(--chakra-colors-sand-600) 0%, var(--chakra-colors-error-fg) 100%)",
      _dark:
        "linear-gradient(90deg, var(--chakra-colors-sand-500) 0%, var(--chakra-colors-error-fg) 100%)",
    },
  },

  avatar: {
    lagoon: {
      _light: "green.300",
      _dark: "green.700",
    },
    glacier: {
      _light: "indigo.300",
      _dark: "indigo.700",
    },
    sky: {
      _light: "sky.400",
      _dark: "sky.600",
    },
    aqua: {
      _light: "aqua.400",
      _dark: "aqua.600",
    },
    coral: {
      _light: "red.400",
      _dark: "red.600",
    },
    meadow: "green.500",
    amber: "sand.500",
    fuchsia: "pink.500",
    orchid: "purple.500",
    denim: {
      _light: "sky.600",
      _dark: "sky.400",
    },
    lake: {
      _light: "aqua.600",
      _dark: "aqua.400",
    },
    crimson: {
      _light: "red.600",
      _dark: "red.400",
    },
    olive: {
      _light: "electricYellow.700",
      _dark: "electricYellow.300",
    },
    rusty: {
      _light: "sand.700",
      _dark: "sand.300",
    },
    ultramarine: {
      _light: "indigo.800",
      _dark: "indigo.200",
    },
    charcoal: {
      _light: "gray.950",
      _dark: "gray.100",
    },
  },

  codeSnippet: {
    sky: {
      _light: "sky.600",
      _dark: "sky.400",
    },
    aqua: {
      _light: "aqua.600",
      _dark: "aqua.400",
    },
    green: {
      _light: "green.600",
      _dark: "green.400",
    },
    electricYellow: {
      _light: "electricYellow.600",
      _dark: "electricYellow.400",
    },
    pink: {
      _light: "pink.600",
      _dark: "pink.400",
    },
    purple: {
      _light: "purple.600",
      _dark: "purple.400",
    },
    indigo: {
      _light: "indigo.600",
      _dark: "indigo.400",
    },
    sand: {
      _light: "sand.600",
      _dark: "sand.400",
    },
  },

  chart: {
    sky: {
      strong: {
        _light: "sky.500",
        _dark: "sky.500",
      },
      medium: {
        _light: "sky.300",
        _dark: "sky.700",
      },
      soft: {
        _light: "sky.100",
        _dark: "sky.900",
      },
      gradient:
        "linear-gradient(to bottom, var(--chakra-colors-chart-sky-soft), var(--chakra-colors-bg-default))",
    },
    sand: {
      strong: {
        _light: "sand.500",
        _dark: "sand.500",
      },
      medium: {
        _light: "sand.300",
        _dark: "sand.700",
      },
      soft: {
        _light: "sand.100",
        _dark: "sand.900",
      },
      gradient:
        "linear-gradient(to bottom, var(--chakra-colors-chart-sand-soft), var(--chakra-colors-bg-default))",
    },
    purple: {
      strong: {
        _light: "purple.500",
        _dark: "purple.500",
      },
      medium: {
        _light: "purple.300",
        _dark: "purple.700",
      },
      soft: {
        _light: "purple.100",
        _dark: "purple.900",
      },
      gradient:
        "linear-gradient(to bottom, var(--chakra-colors-chart-purple-soft), var(--chakra-colors-bg-default))",
    },
    aqua: {
      strong: {
        _light: "aqua.500",
        _dark: "aqua.500",
      },
      medium: {
        _light: "aqua.300",
        _dark: "aqua.700",
      },
      soft: {
        _light: "aqua.100",
        _dark: "aqua.900",
      },
      gradient:
        "linear-gradient(to bottom, var(--chakra-colors-chart-aqua-soft), var(--chakra-colors-bg-default))",
    },
    red: {
      strong: {
        _light: "red.500",
        _dark: "red.500",
      },
      medium: {
        _light: "red.300",
        _dark: "red.700",
      },
      soft: {
        _light: "red.100",
        _dark: "red.900",
      },
      gradient:
        "linear-gradient(to bottom, var(--chakra-colors-chart-red-soft), var(--chakra-colors-bg-default))",
    },
    pink: {
      strong: {
        _light: "pink.500",
        _dark: "pink.500",
      },
      medium: {
        _light: "pink.300",
        _dark: "pink.700",
      },
      soft: {
        _light: "pink.100",
        _dark: "pink.900",
      },
      gradient:
        "linear-gradient(to bottom, var(--chakra-colors-chart-pink-soft), var(--chakra-colors-bg-default))",
    },
    indigo: {
      strong: {
        _light: "indigo.500",
        _dark: "indigo.500",
      },
      medium: {
        _light: "indigo.300",
        _dark: "indigo.700",
      },
      soft: {
        _light: "indigo.100",
        _dark: "indigo.900",
      },
      gradient:
        "linear-gradient(to bottom, var(--chakra-colors-chart-indigo-soft), var(--chakra-colors-bg-default))",
    },
    // Neutral, so contrast against the background flips between modes.
    gray: {
      strong: {
        _light: "gray.700",
        _dark: "gray.300",
      },
      medium: {
        _light: "gray.500",
        _dark: "gray.500",
      },
      soft: {
        _light: "gray.300",
        _dark: "gray.700",
      },
      gradient:
        "linear-gradient(to bottom, var(--chakra-colors-chart-gray-soft), var(--chakra-colors-bg-default))",
    },
  },

  storage: {
    rowstore: {
      _light: "sky.200",
      _dark: "sky.400",
    },
    columnstore: {
      _light: "indigo.600",
      _dark: "indigo.600",
    },
    tiered: {
      _light: "aqua.400",
      _dark: "aqua.500",
    },
  },

  tab: {
    bg: {
      _light: "gray.100",
      _dark: "black",
    },
  },

  diagramTile: {
    bg: {
      inactive: {
        _light: "gray.50",
        _dark: "whiteAlpha.100",
      },
      active: {
        _light: "white",
        _dark: "gray.950",
      },
    },
  },

  // The code fields on the service overview's Service Info card. Light keeps
  // the inverted look; dark lifts them off the near-black card to gray.
  serviceInfoInput: {
    bg: {
      _light: "black",
      _dark: "gray.700",
    },
  },

  tooltip: {
    bg: {
      _light: "gray.100",
      _dark: "gray.900",
    },
  },

  illustration: {
    fg: {
      _light: "black",
      _dark: "gray.50",
    },
    fgMuted: {
      _light: "gray.700",
      _dark: "gray.400",
    },
    fgSubtle: "gray.500",
    fgInverted: {
      _light: "white",
      _dark: "gray.950",
    },
    border: {
      _light: "gray.200",
      _dark: "gray.800",
    },
    surface: {
      _light: "gray.50",
      _dark: "gray.950",
    },
    gridLine: {
      _light: "gray.100",
      _dark: "gray.900",
    },
    gridDashed: {
      _light: "gray.300",
      _dark: "gray.700",
    },
    accent: {
      _light: "indigo.500",
      _dark: "indigo.500",
    },
    accentSoft: {
      _light: "indigo.200",
      _dark: "indigo.800",
    },
    accentTint: {
      _light: "indigo.100",
      _dark: "indigo.900",
    },
    accentToFg: {
      _light: "indigo.500",
      _dark: "gray.50",
    },
    sky: {
      _light: "sky.300",
      _dark: "sky.600",
    },
    error: "red.500",
    errorSoft: {
      _light: "red.400",
      _dark: "red.600",
    },
  },

  button: {
    default: {
      _light: "gray.100",
      _dark: "gray.900",
    },
    subtle: {
      _light: "gray.200",
      _dark: "gray.800",
    },
    muted: {
      _light: "gray.400",
      _dark: "gray.600",
    },
    emphasized: {
      _light: "gray.700",
      _dark: "gray.200",
    },
    solid: {
      _light: "gray.900",
      _dark: "indigo.100",
    },
    inverted: {
      default: {
        bg: "white",
        fg: "black",
        border: "black",
      },
      hover: {
        bg: "gray.800",
        fg: "white",
        border: "gray.800",
      },
      focus: {
        bg: "gray.700",
        fg: "white",
        border: "black",
      },
      disabled: {
        bg: "gray.100",
        fg: "gray.400",
        border: "gray.100",
      },
    },
  },

  switch: {
    thumb: "white",
  },

  tag: {
    sky: {
      fg: {
        _light: "sky.600",
        _dark: "sky.400",
      },
      bg: {
        _light: "sky.100",
        _dark: "sky.900",
      },
    },
    green: {
      fg: {
        _light: "green.600",
        _dark: "green.400",
      },
      bg: {
        _light: "green.100",
        _dark: "green.900",
      },
    },
    pink: {
      fg: {
        _light: "pink.600",
        _dark: "pink.400",
      },
      bg: {
        _light: "pink.100",
        _dark: "pink.900",
      },
    },
  },

  dialog: {
    backdrop: "blackAlpha.600",
  },
};
